// src/utils/boardHelpers.js
// Consolidated helper functions for Project Board

// ============ AVATAR & ATTACHMENT URL HELPERS ============

/**
 * Get avatar URL with cache-busting for fresh loads
 * @param {string} avatarPath - Path to avatar image
 * @returns {string|null} - Full URL or null
 */
export const getAvatarUrl = (avatarPath) => {
  if (!avatarPath) return null;
  const baseUrl =
    process.env.REACT_APP_AVATAR_URL?.replace(/\/+$/, "") ||
    "http://localhost:8000/storage";
  const cleanPath = avatarPath.replace(/^\/+/, "");
  // Cache-busting: force fresh load to avoid stale avatars
  return `${baseUrl}/${cleanPath}?t=${Date.now()}`;
};

/**
 * Get attachment URL (no cache-busting - attachments are versioned by backend)
 * @param {object} attachment - Attachment object with file_path/path/url
 * @returns {string|null} - Full URL or null
 */
export const getAttachmentUrl = (attachment) => {
  if (!attachment) return null;
  const filePath =
    attachment.file_path || attachment.path || attachment.url || "";
  if (!filePath) return null;
  // Return external URLs as-is
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  const baseUrl =
    process.env.REACT_APP_API_URL?.replace(/\/+$/, "") ||
    "http://localhost:8000";
  const cleanPath = filePath.replace(/^\/+/, "");
  return `${baseUrl}/storage/${cleanPath}`;
};

// ============ TIME FORMATTING ============

/**
 * Format date to human-readable "time ago" string
 * @param {string} dateStr - ISO date string
 * @returns {string} - e.g., "5m ago", "2h ago", "3d ago"
 */
export const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "";
  const now = new Date();
  const commentDate = new Date(dateStr);
  const diffSec = Math.floor((now - commentDate) / 1000);

  if (diffSec < 0) return "Just now";
  if (diffSec < 60) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

// ============ IMAGE PASTE HANDLER (for comments) ============

/**
 * Handle pasted images from clipboard into comment files
 * @param {ClipboardEvent} event - Clipboard paste event
 * @param {string|number} taskId - Current task ID
 * @param {function} setCommentFiles - State setter for comment files
 * @param {object} toast - Toast notification object (optional)
 */
export const handlePasteImage = (event, taskId, setCommentFiles, toast) => {
  const items = event.clipboardData?.items;
  if (!items) return;

  const imageFiles = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].type?.indexOf("image") !== -1) {
      const file = items[i].getAsFile();
      if (file) imageFiles.push(file);
    }
  }

  if (imageFiles.length > 0) {
    event.preventDefault();
    setCommentFiles((prev) => {
      const existingFiles = prev[taskId] || [];
      const existingArray = existingFiles instanceof FileList
        ? Array.from(existingFiles)
        : existingFiles;
      return {
        ...prev,
        [taskId]: [...existingArray, ...imageFiles],
      };
    });
    toast?.success(`📸 ${imageFiles.length} image(s) pasted`);
  }
};

// ============ LAZY IMAGE LOADING UTILITIES ============

/**
 * Create an image with lazy loading support
 * @param {string} src - Image source URL
 * @param {function} callback - Callback(err, img) when loaded
 * @returns {HTMLImageElement} - The image element
 */
export const createImageWithLazyLoad = (src, callback) => {
  const img = new Image();
  img.loading = "lazy"; // Native lazy loading hint
  img.onload = () => callback(null, img);
  img.onerror = (err) => callback(err || new Error("Failed to load image"), null);
  img.src = src;
  return img;
};

/**
 * Intersection Observer-based lazy load hook helper
 * Use this with React hooks for viewport-based loading
 * @param {string} src - Image URL
 * @param {string} placeholder - Placeholder data URL or color
 * @returns {object} - { loaded, src, error }
 */
export const useLazyImage = (src, placeholder = null) => {
  // This is a utility description - implement as a custom hook in your components
  // Example usage in a component:
  /*
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.onload = () => setLoaded(true);
    img.onerror = () => setError(true);
    img.src = src;
    return () => { img.onload = null; img.onerror = null; };
  }, [src]);
  
  return {
    src: loaded ? src : placeholder,
    loaded,
    error
  };
  */
  return { src, loaded: true, error: false }; // Placeholder return
};

// ============ FILE VALIDATION ============

/**
 * Allowed file types for uploads (MIME -> extensions mapping)
 */
export const ALLOWED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/plain': ['.txt'],
  // Video types added
  'video/mp4': ['.mp4'],
  'video/mpeg': ['.mpeg'],
  'video/quicktime': ['.mov'],
  'video/x-msvideo': ['.avi'],
  'video/webm': ['.webm']
};

// ✅ 10MB Limit in Bytes
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const ACCEPTED_MIME_TYPES = Object.keys(ALLOWED_FILE_TYPES).join(',');
export const ACCEPTED_EXTENSIONS_LIST = Object.values(ALLOWED_FILE_TYPES).flat();

export const validateFile = (file) => {
  if (!(file instanceof File)) {
    return { valid: false, reason: "Not a valid File object" };
  }

  // ✅ 1. Check File Size (10MB)
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      reason: `File size exceeds 10MB limit. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`
    };
  }

  const extension = '.' + file.name.split('.').pop().toLowerCase();
  const mimeType = file.type.toLowerCase();

  // ✅ 2. Check Extension
  const isValidExtension = ACCEPTED_EXTENSIONS_LIST.includes(extension);

  // ✅ 3. Check MIME Type
  const isValidMimeType = Object.keys(ALLOWED_FILE_TYPES).some(
    type => mimeType === type || mimeType.startsWith(type.split('/')[0] + '/')
  );

  if (!isValidExtension) {
    return { valid: false, reason: `Invalid extension: ${extension}` };
  }
  if (!isValidMimeType) {
    return { valid: false, reason: `Invalid file type: ${mimeType}` };
  }

  return { valid: true };
};

/**
 * Validate multiple files and return results
 * @param {File[]} files - Array of File objects
 * @returns {object} - { validFiles: File[], invalidFiles: Array<{file, reason}> }
 */
export const validateFiles = (files) => {
  const validFiles = [];
  const invalidFiles = [];

  files.forEach((file) => {
    const result = validateFile(file);
    if (result.valid) {
      validFiles.push(file);
    } else {
      invalidFiles.push({ file, reason: result.reason });
    }
  });

  return { validFiles, invalidFiles };
};

// ============ BOARD-SPECIFIC UTILITIES ============

/**
 * Generate a safe key from status name for column IDs
 * @param {string} name - Status name
 * @returns {string} - Safe key (lowercase, underscores, no special chars)
 */
export const generateStatusKey = (name) => {
  if (!name) return "unknown";
  return name
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
};

/**
 * Check if a task is overdue based on due date and status
 * @param {object} task - Task object with due_date and statusObj
 * @returns {boolean} - True if overdue and not completed
 */
export const isTaskOverdue = (task) => {
  if (!task?.due_date) return false;
  const dueDate = new Date(task.due_date);
  const now = new Date();
  const isCompleted = ["completed", "done", "closed"].includes(
    task.statusObj?.key?.toLowerCase()
  );
  return now > dueDate && !isCompleted;
};

/**
 * Get effective status key for a task (handles overdue -> backlog logic)
 * @param {object} task - Task object
 * @param {string} backlogStatusId - ID of backlog status
 * @param {Array} taskStatuses - All available statuses
 * @returns {string} - Effective column key for rendering
 */
export const getEffectiveStatusKey = (task, backlogStatusId, taskStatuses) => {
  // Fallback chain for status resolution
  const statusObj = task.statusObj || task.status || taskStatuses.find(s => String(s.id) === String(task.status_id)) || null;

  let statusKey = statusObj?.key?.toLowerCase();
  if (!statusKey && statusObj?.name) {
    statusKey = generateStatusKey(statusObj.name);
  }
  if (!statusKey) statusKey = "backlog";

  const isOverdue = isTaskOverdue(task);
  const isCompleted = ["completed", "done", "closed"].includes(statusKey);
  const hasNoStatus = !task.status_id;

  // Move to backlog if: overdue (not completed) OR no status OR already backlog
  if (
    (isOverdue && task.status_id !== backlogStatusId && !isCompleted) ||
    hasNoStatus ||
    task.status_id === backlogStatusId
  ) {
    return "backlog";
  }

  return statusKey;
};

// ============ EXPORT ALL ============
export default {
  getAvatarUrl,
  getAttachmentUrl,
  formatTimeAgo,
  handlePasteImage,
  createImageWithLazyLoad,
  useLazyImage,
  ALLOWED_FILE_TYPES,
  ACCEPTED_MIME_TYPES,
  ACCEPTED_EXTENSIONS_LIST,
  validateFile,
  validateFiles,
  generateStatusKey,
  isTaskOverdue,
  getEffectiveStatusKey,
};